import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatIDR } from "@/lib/month";
import { BedDouble, Loader2, Plus, Trash2, TrendingUp, XCircle, Check, Target, Gift } from "lucide-react";

const AGENTS = [
  { id: "agoda", label: "Agoda" },
  { id: "airbnb", label: "Airbnb" },
  { id: "booking", label: "Booking" },
  { id: "expedia", label: "Expedia" },
  { id: "traveloka", label: "Traveloka" },
  { id: "tiket", label: "Tiket" },
  { id: "others", label: "Others" },
];

const emptyGrid = () => Object.fromEntries(AGENTS.map((a) => [a.id, { res: 0, revenue: 0 }]));
const todayWIB = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const agentLabel = (id) => AGENTS.find((a) => a.id === id)?.label || id;

function GridTable({ title, icon: Icon, grid, dark, prefix, accent }) {
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const totRes = AGENTS.reduce((s, a) => s + (Number(grid[a.id]?.res) || 0), 0);
  const totRev = AGENTS.reduce((s, a) => s + (Number(grid[a.id]?.revenue) || 0), 0);
  return (
    <div className={`rounded-2xl border p-5 shadow-sm ${dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white"}`} data-testid={`${prefix}-grid`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dark ? accent.darkBg : accent.lightBg}`}>
          <Icon className={`h-5 w-5 ${dark ? accent.darkText : accent.lightText}`} />
        </div>
        <h2 className={`font-display font-semibold text-lg ${dark ? "text-white" : "text-slate-900"}`}>{title}</h2>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className={`text-[11px] uppercase tracking-wide ${sub}`}>
            <th className="text-left pb-2 font-medium">Agent</th>
            <th className="text-right pb-2 font-medium w-24">Reservation</th>
            <th className="text-right pb-2 font-medium w-36">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {AGENTS.map((a) => {
            const cell = grid[a.id] || { res: 0, revenue: 0 };
            return (
              <tr key={a.id} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`} data-testid={`${prefix}-row-${a.id}`}>
                <td className={`py-2 font-medium ${dark ? "text-slate-200" : "text-slate-800"}`}>{a.label}</td>
                <td className={`py-2 text-right font-mono ${dark ? "text-slate-300" : "text-slate-700"}`} data-testid={`${prefix}-res-${a.id}`}>{cell.res || 0}</td>
                <td className={`py-2 text-right font-mono ${dark ? "text-slate-300" : "text-slate-700"}`} data-testid={`${prefix}-rev-${a.id}`}>{formatIDR(cell.revenue || 0)}</td>
              </tr>
            );
          })}
          <tr className={`border-t-2 ${dark ? "border-slate-700" : "border-slate-300"}`}>
            <td className={`py-2.5 font-bold ${dark ? "text-white" : "text-slate-900"}`}>TOTAL</td>
            <td className={`py-2.5 text-right font-mono font-bold ${dark ? "text-white" : "text-slate-900"}`} data-testid={`${prefix}-total-res`}>{totRes}</td>
            <td className={`py-2.5 text-right font-mono font-bold ${dark ? accent.darkText : accent.lightText}`} data-testid={`${prefix}-total-rev`}>{formatIDR(totRev)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function ReservationReportPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const dark = isAdmin;
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const inputCls = dark ? "bg-slate-800/60 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-900";
  const [mode, setMode] = useState("daily");
  const [date, setDate] = useState(todayWIB());
  const [month, setMonth] = useState(todayWIB().slice(0, 7));
  const [propFilter, setPropFilter] = useState("all");
  const [properties, setProperties] = useState([]);
  const [pickup, setPickup] = useState(emptyGrid());
  const [cancelled, setCancelled] = useState(emptyGrid());
  const [entries, setEntries] = useState([]);
  const [days, setDays] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ kind: "pickup", agent: "agoda", property_id: "", revenue: "" });
  const [checklist, setChecklist] = useState(null);
  const [addons, setAddons] = useState({ entries: [], target: 0, achieved: 0, pct: null });
  const [addonForm, setAddonForm] = useState({ date: todayWIB(), desc: "", property_id: "", revenue: "" });
  const [targetInput, setTargetInput] = useState("");
  const [savingTarget, setSavingTarget] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === "daily" ? { date } : { month };
      if (propFilter !== "all") params.property_id = propFilter;
      const { data } = await api.get("/reservations/summary", { params });
      setPickup({ ...emptyGrid(), ...data.pickup });
      setCancelled({ ...emptyGrid(), ...data.cancelled });
      setDays(data.days || 0);
      if (mode === "daily") {
        const ep = { date };
        if (propFilter !== "all") ep.property_id = propFilter;
        const [res, chk] = await Promise.all([
          api.get("/reservations/entries", { params: ep }),
          api.get("/reservations/checklist", { params: { date } }),
        ]);
        setEntries(res.data.entries);
        setChecklist(chk.data);
      } else {
        setEntries([]);
        const { data: ad } = await api.get("/reservations/addons", { params: { month } });
        setAddons(ad);
        setTargetInput(ad.target ? String(ad.target) : "");
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  }, [mode, date, month, propFilter]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/properties").then(({ data }) => {
      setProperties(data.properties);
      if (data.properties.length) {
        setForm((f) => ({ ...f, property_id: f.property_id || data.properties[0].id }));
        setAddonForm((f) => ({ ...f, property_id: f.property_id || data.properties[0].id }));
      }
    }).catch(() => {});
  }, []);

  const addEntry = async () => {
    if (!form.property_id) return toast.error("Pilih property dulu");
    setSaving(true);
    try {
      await api.post("/reservations/entries", {
        date, agent: form.agent, kind: form.kind,
        revenue: Number(form.revenue) || 0, property_id: form.property_id,
      });
      toast.success(`${form.kind === "pickup" ? "Pickup" : "Pembatalan"} ${agentLabel(form.agent)} tercatat`);
      setForm((f) => ({ ...f, revenue: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delEntry = async (id) => {
    try {
      await api.delete(`/reservations/entries/${id}`);
      toast.success("Entry dihapus");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleChecklist = async (itemId, done) => {
    try {
      await api.put("/reservations/checklist", { date, item: itemId, done });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const addAddon = async () => {
    if (!addonForm.desc.trim()) return toast.error("Isi deskripsi add-on dulu");
    if (!addonForm.property_id) return toast.error("Pilih property dulu");
    setSaving(true);
    try {
      await api.post("/reservations/addons", {
        date: addonForm.date, desc: addonForm.desc,
        revenue: Number(addonForm.revenue) || 0, property_id: addonForm.property_id,
      });
      toast.success("Add-on tercatat");
      setAddonForm((f) => ({ ...f, desc: "", revenue: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const delAddon = async (id) => {
    try {
      await api.delete(`/reservations/addons/${id}`);
      toast.success("Add-on dihapus");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveTarget = async () => {
    setSavingTarget(true);
    try {
      await api.put("/admin/reservations/addon-target", { month, amount: Number(targetInput) || 0 });
      toast.success("Target add-ons tersimpan");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingTarget(false); }
  };

  const accentPickup = { darkBg: "bg-emerald-500/15", lightBg: "bg-emerald-100", darkText: "text-emerald-400", lightText: "text-emerald-700" };
  const accentCancel = { darkBg: "bg-rose-500/15", lightBg: "bg-rose-100", darkText: "text-rose-400", lightText: "text-rose-700" };

  return (
    <div className={`min-h-screen ${dark ? "bg-[#14110F] dark-scroll text-slate-100" : "bg-[#FAF7F2] text-slate-900"}`}>
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="reservation-report-page">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className={`font-display text-2xl font-bold flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <BedDouble className={`h-6 w-6 ${dark ? "text-amber-400" : "text-amber-600"}`} /> Reservation Report
            </h1>
            <p className={`text-sm ${sub}`}>Input per-reservasi — summary Pick up &amp; Cancelled per agent terhitung otomatis</p>
          </div>
          <div className={`flex rounded-lg border overflow-hidden ${dark ? "border-slate-700" : "border-slate-300"}`} data-testid="report-mode-toggle">
            <button onClick={() => setMode("daily")} className={`px-4 h-9 text-xs font-medium ${mode === "daily" ? (dark ? "bg-amber-500/20 text-amber-300" : "bg-amber-100 text-amber-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`} data-testid="mode-daily">Daily</button>
            <button onClick={() => setMode("monthly")} className={`px-4 h-9 text-xs font-medium border-l ${dark ? "border-slate-700" : "border-slate-300"} ${mode === "monthly" ? (dark ? "bg-teal-500/20 text-teal-300" : "bg-teal-100 text-teal-700") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`} data-testid="mode-monthly">Monthly</button>
          </div>
        </div>

        <div className={`rounded-2xl border p-5 mb-5 flex items-end gap-4 flex-wrap ${dark ? "border-slate-800 bg-slate-900/60" : "border-slate-200 bg-white"}`} data-testid="report-filter-bar">
          {mode === "daily" ? (
            <div className="space-y-1.5">
              <Label className={`text-xs ${sub}`}>Tanggal</Label>
              <Input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} className={`w-44 ${inputCls}`} data-testid="report-date-input" />
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className={`text-xs ${sub}`}>Bulan</Label>
              <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className={`w-44 ${inputCls}`} data-testid="report-month-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className={`text-xs ${sub}`}>Property</Label>
            <select value={propFilter} onChange={(e) => setPropFilter(e.target.value)} className={`h-9 w-52 rounded-md border px-2 text-sm ${inputCls}`} data-testid="report-property-filter">
              <option value="all">All properties</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {mode === "monthly" && <p className={`text-xs ${sub}`} data-testid="monthly-days">Akumulasi {days} hari berisi data</p>}
        </div>

        {mode === "daily" && (
          <div className={`rounded-2xl border p-5 mb-5 ${dark ? "border-amber-800/40 bg-gradient-to-r from-slate-900 to-slate-900/60" : "border-amber-200 bg-white"}`} data-testid="entry-form">
            <p className={`text-xs font-medium mb-3 ${sub}`}>Input reservasi baru (tanggal mengikuti tanggal report di atas)</p>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="space-y-1.5">
                <Label className={`text-xs ${sub}`}>Tipe</Label>
                <div className={`flex rounded-lg border overflow-hidden ${dark ? "border-slate-700" : "border-slate-300"}`}>
                  <button onClick={() => setForm((f) => ({ ...f, kind: "pickup" }))} className={`px-3 h-9 text-xs font-medium ${form.kind === "pickup" ? (dark ? "bg-emerald-500/20 text-emerald-300" : "bg-emerald-100 text-emerald-700") : (dark ? "text-slate-400" : "text-slate-500")}`} data-testid="entry-kind-pickup">Pick up</button>
                  <button onClick={() => setForm((f) => ({ ...f, kind: "cancel" }))} className={`px-3 h-9 text-xs font-medium border-l ${dark ? "border-slate-700" : "border-slate-300"} ${form.kind === "cancel" ? (dark ? "bg-rose-500/20 text-rose-300" : "bg-rose-100 text-rose-700") : (dark ? "text-slate-400" : "text-slate-500")}`} data-testid="entry-kind-cancel">Cancelled</button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className={`text-xs ${sub}`}>Agent / OTA</Label>
                <select value={form.agent} onChange={(e) => setForm((f) => ({ ...f, agent: e.target.value }))} className={`h-9 w-36 rounded-md border px-2 text-sm ${inputCls}`} data-testid="entry-agent-select">
                  {AGENTS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className={`text-xs ${sub}`}>Property</Label>
                <select value={form.property_id} onChange={(e) => setForm((f) => ({ ...f, property_id: e.target.value }))} className={`h-9 w-48 rounded-md border px-2 text-sm ${inputCls}`} data-testid="entry-property-select">
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className={`text-xs ${sub}`}>Revenue (Rp)</Label>
                <Input type="number" min="0" value={form.revenue} onChange={(e) => setForm((f) => ({ ...f, revenue: e.target.value }))} placeholder="0" className={`w-36 ${inputCls}`} data-testid="entry-revenue-input" />
              </div>
              <Button onClick={addEntry} disabled={saving} className={dark ? "bg-amber-600 hover:bg-amber-500 text-white" : ""} data-testid="entry-add-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Tambah
              </Button>
            </div>
          </div>
        )}

        {mode === "daily" && checklist && (
          <div className={`rounded-2xl border p-5 mb-5 ${dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white"}`} data-testid="checklist-card">
            <h3 className={`font-display font-semibold mb-3 flex items-center gap-2 ${dark ? "text-white" : "text-slate-900"}`}>
              <Check className={`h-4 w-4 ${dark ? "text-teal-400" : "text-teal-600"}`} /> Checklist harian
            </h3>
            <div className="grid sm:grid-cols-2 gap-2.5">
              {Object.entries(checklist.labels).map(([id, label]) => {
                const it = checklist.items[id] || {};
                return (
                  <button key={id} onClick={() => toggleChecklist(id, !it.done)}
                    className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
                      it.done
                        ? dark ? "border-emerald-500/40 bg-emerald-500/10" : "border-emerald-300 bg-emerald-50"
                        : dark ? "border-slate-700 hover:border-slate-600" : "border-slate-200 hover:border-slate-300"
                    }`}
                    data-testid={`checklist-item-${id}`}>
                    <span className={`h-5 w-5 rounded-md border flex items-center justify-center flex-shrink-0 ${
                      it.done ? "bg-emerald-500 border-emerald-500 text-white" : dark ? "border-slate-600" : "border-slate-300"
                    }`}>
                      {it.done && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="flex-1">
                      <span className={`block text-sm font-medium ${dark ? "text-slate-100" : "text-slate-800"}`}>{label}</span>
                      {it.done && it.by && <span className={`block text-[11px] ${sub}`}>✓ {it.by} · {new Date(it.at).toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}</span>}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-amber-400" /></div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid lg:grid-cols-2 gap-5 mb-5">
              <GridTable title="Pick up today" icon={TrendingUp} grid={pickup} dark={dark} prefix="pickup" accent={accentPickup} />
              <GridTable title="Cancelled today" icon={XCircle} grid={cancelled} dark={dark} prefix="cancelled" accent={accentCancel} />
            </motion.div>

            {mode === "monthly" && (
              <div className={`rounded-2xl border p-5 ${dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white"}`} data-testid="addons-card">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${dark ? "bg-teal-500/15" : "bg-teal-100"}`}>
                    <Gift className={`h-5 w-5 ${dark ? "text-teal-400" : "text-teal-700"}`} />
                  </div>
                  <h2 className={`font-display font-semibold text-lg ${dark ? "text-white" : "text-slate-900"}`}>Add-ons</h2>
                </div>

                <div className={`rounded-xl border p-4 mb-4 ${dark ? "border-slate-800 bg-slate-800/40" : "border-slate-100 bg-slate-50"}`} data-testid="addon-progress">
                  <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                    <div className="flex items-center gap-2">
                      <Target className={`h-4 w-4 ${dark ? "text-amber-400" : "text-amber-600"}`} />
                      <span className={`text-sm font-medium ${dark ? "text-slate-200" : "text-slate-800"}`} data-testid="addon-achieved-label">
                        {formatIDR(addons.achieved)} / {addons.target > 0 ? formatIDR(addons.target) : "target belum diset"}
                      </span>
                    </div>
                    {addons.pct !== null && <span className={`text-sm font-bold ${dark ? "text-amber-400" : "text-amber-600"}`} data-testid="addon-pct">{addons.pct}%</span>}
                  </div>
                  <div className={`h-2.5 rounded-full overflow-hidden ${dark ? "bg-slate-700" : "bg-slate-200"}`}>
                    <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: `${Math.min(100, addons.pct || 0)}%` }} data-testid="addon-progress-bar" />
                  </div>
                  {isAdmin && (
                    <div className="flex items-end gap-2 mt-3">
                      <div className="space-y-1">
                        <Label className={`text-[11px] ${sub}`}>Target bulan ini (Rp)</Label>
                        <Input type="number" min="0" value={targetInput} onChange={(e) => setTargetInput(e.target.value)} className={`h-8 w-44 ${inputCls}`} data-testid="addon-target-input" />
                      </div>
                      <Button size="sm" onClick={saveTarget} disabled={savingTarget} className={dark ? "bg-amber-600 hover:bg-amber-500 text-white" : ""} data-testid="addon-target-save">
                        {savingTarget ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set target"}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex items-end gap-3 flex-wrap mb-4" data-testid="addon-form">
                  <div className="space-y-1.5">
                    <Label className={`text-xs ${sub}`}>Date</Label>
                    <Input type="date" value={addonForm.date} onChange={(e) => e.target.value && setAddonForm((f) => ({ ...f, date: e.target.value }))} className={`w-40 ${inputCls}`} data-testid="addon-date-input" />
                  </div>
                  <div className="space-y-1.5 flex-1 min-w-[160px]">
                    <Label className={`text-xs ${sub}`}>Desc</Label>
                    <Input value={addonForm.desc} onChange={(e) => setAddonForm((f) => ({ ...f, desc: e.target.value }))} placeholder="Misal: early check-in, extra bed" className={inputCls} data-testid="addon-desc-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className={`text-xs ${sub}`}>Property</Label>
                    <select value={addonForm.property_id} onChange={(e) => setAddonForm((f) => ({ ...f, property_id: e.target.value }))} className={`h-9 w-44 rounded-md border px-2 text-sm ${inputCls}`} data-testid="addon-property-select">
                      {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className={`text-xs ${sub}`}>Revenue (Rp)</Label>
                    <Input type="number" min="0" value={addonForm.revenue} onChange={(e) => setAddonForm((f) => ({ ...f, revenue: e.target.value }))} placeholder="0" className={`w-32 ${inputCls}`} data-testid="addon-revenue-input" />
                  </div>
                  <Button onClick={addAddon} disabled={saving} className={dark ? "bg-amber-600 hover:bg-amber-500 text-white" : ""} data-testid="addon-add-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Tambah
                  </Button>
                </div>

                {addons.entries.length === 0 ? (
                  <p className={`text-sm italic ${sub}`} data-testid="addons-empty">Belum ada add-on bulan ini.</p>
                ) : (
                  <table className="w-full text-sm" data-testid="addons-table">
                    <thead>
                      <tr className={`text-[11px] uppercase tracking-wide ${sub}`}>
                        <th className="text-left pb-2 font-medium w-28">Date</th>
                        <th className="text-left pb-2 font-medium">Desc</th>
                        <th className="text-left pb-2 font-medium">Property</th>
                        <th className="text-right pb-2 font-medium w-36">Revenue</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {addons.entries.map((a) => (
                        <tr key={a.id} className={`border-t ${dark ? "border-slate-800" : "border-slate-100"}`} data-testid={`addon-row-${a.id}`}>
                          <td className={`py-2 font-mono text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>{a.date}</td>
                          <td className={`py-2 ${dark ? "text-slate-200" : "text-slate-800"}`}>{a.desc}</td>
                          <td className={`py-2 text-xs ${sub}`}>{a.property_name}</td>
                          <td className={`py-2 text-right font-mono ${dark ? "text-slate-300" : "text-slate-700"}`}>{formatIDR(a.revenue)}</td>
                          <td className="py-2 text-right">
                            <button onClick={() => delAddon(a.id)} className={`${dark ? "text-slate-600 hover:text-rose-400" : "text-slate-400 hover:text-rose-500"}`} data-testid={`addon-delete-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr className={`border-t-2 ${dark ? "border-slate-700" : "border-slate-300"}`}>
                        <td colSpan={3} className={`py-2.5 font-bold ${dark ? "text-white" : "text-slate-900"}`}>TOTAL</td>
                        <td className={`py-2.5 text-right font-mono font-bold ${dark ? "text-teal-400" : "text-teal-700"}`} data-testid="addon-total-revenue">{formatIDR(addons.achieved)}</td>
                        <td></td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {mode === "daily" && (
              <div className={`rounded-2xl border p-5 ${dark ? "border-slate-800 bg-slate-900/90" : "border-slate-200 bg-white"}`} data-testid="entries-card">
                <h3 className={`font-display font-semibold mb-3 ${dark ? "text-white" : "text-slate-900"}`}>Entries hari ini ({entries.length})</h3>
                {entries.length === 0 ? (
                  <p className={`text-sm italic ${sub}`} data-testid="entries-empty">Belum ada reservasi tercatat untuk tanggal ini.</p>
                ) : (
                  <div className="space-y-1.5">
                    {entries.map((e) => (
                      <div key={e.id} className={`flex items-center gap-3 text-sm rounded-lg border px-3 py-2 ${dark ? "border-slate-800 bg-slate-800/40" : "border-slate-100 bg-slate-50"}`} data-testid={`entry-row-${e.id}`}>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${e.kind === "pickup" ? (dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700") : (dark ? "bg-rose-500/15 text-rose-300" : "bg-rose-100 text-rose-700")}`}>
                          {e.kind === "pickup" ? "Pick up" : "Cancel"}
                        </span>
                        <span className={`font-medium ${dark ? "text-slate-200" : "text-slate-800"}`}>{agentLabel(e.agent)}</span>
                        <span className={`text-xs ${sub}`}>{e.property_name}</span>
                        <span className={`ml-auto font-mono ${dark ? "text-slate-300" : "text-slate-700"}`}>{formatIDR(e.revenue)}</span>
                        <span className={`text-[11px] ${sub}`}>{e.staff_name}</span>
                        <button onClick={() => delEntry(e.id)} className={`${dark ? "text-slate-600 hover:text-rose-400" : "text-slate-400 hover:text-rose-500"}`} data-testid={`entry-delete-${e.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
